import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { lookupSignalAvatar } from './signalAvatars';

// Default avatar icons - fun team mascots
const AVATAR_OPTIONS = [
  { id: 'rocket', emoji: '🚀', name: 'Rocket' },
  { id: 'fire', emoji: '🔥', name: 'Fire' },
  { id: 'star', emoji: '⭐', name: 'Star' },
  { id: 'bolt', emoji: '⚡', name: 'Bolt' },
  { id: 'diamond', emoji: '💎', name: 'Diamond' },
  { id: 'crown', emoji: '👑', name: 'Crown' },
  { id: 'trophy', emoji: '🏆', name: 'Trophy' },
  { id: 'target', emoji: '🎯', name: 'Target' },
  { id: 'brain', emoji: '🧠', name: 'Brain' },
  { id: 'light', emoji: '💡', name: 'Light' },
  { id: 'muscle', emoji: '💪', name: 'Muscle' },
  { id: 'heart', emoji: '❤️', name: 'Heart' },
  { id: 'ninja', emoji: '🥷', name: 'Ninja' },
  { id: 'dragon', emoji: '🐉', name: 'Dragon' },
  { id: 'unicorn', emoji: '🦄', name: 'Unicorn' },
  { id: 'phoenix', emoji: '🔶', name: 'Phoenix' },
];

// Get avatar for team number (deterministic but can be overridden)
export const getDefaultAvatar = (teamNumber) => {
  const index = (teamNumber - 1) % AVATAR_OPTIONS.length;
  return AVATAR_OPTIONS[index];
};

// Team Avatar Display Component
export const TeamAvatar = ({ 
  teamNumber, 
  avatarId = null, 
  size = 'md', 
  showBorder = true,
  onClick = null,
  className = ''
}) => {
  const { themeConfig } = useTheme();
  
  // Get avatar - try AVATAR_OPTIONS first, then signal-based avatars (auto-assigned to teams),
  // and finally fall back to deterministic team-number default.
  const avatar = avatarId
    ? (AVATAR_OPTIONS.find(a => a.id === avatarId) || lookupSignalAvatar(avatarId) || getDefaultAvatar(teamNumber))
    : getDefaultAvatar(teamNumber);
  
  const sizeClasses = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-3xl',
    xl: 'w-20 h-20 text-4xl',
  };
  
  return (
    <div
      className={`
        ${sizeClasses[size] || sizeClasses.md}
        rounded-full flex items-center justify-center
        ${showBorder ? 'ring-2 ring-offset-2 ring-offset-transparent' : ''}
        ${onClick ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
        ${className}
      `}
      style={{
        background: themeConfig?.colors?.gradient || 'linear-gradient(135deg, #E91E8C 0%, #8B5CF6 100%)',
        ringColor: themeConfig?.colors?.primary || '#E91E8C',
      }}
      onClick={onClick}
      title={avatar.name}
    >
      <span className="drop-shadow-lg">{avatar.emoji}</span>
    </div>
  );
};

// Avatar Selector Component (for editing)
export const AvatarSelector = ({ 
  currentAvatarId, 
  onSelect, 
  onClose 
}) => {
  const { themeConfig, isDark } = useTheme();
  
  return (
    <div className={`
      p-4 rounded-2xl backdrop-blur-xl
      ${isDark ? 'bg-black/80' : 'bg-white/90'}
      border border-white/10 shadow-2xl
    `}>
      <p className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
        Choose Team Avatar
      </p>
      <div className="grid grid-cols-4 gap-2">
        {AVATAR_OPTIONS.map((avatar) => (
          <button
            key={avatar.id}
            onClick={() => {
              onSelect(avatar.id);
              onClose?.();
            }}
            className={`
              w-12 h-12 rounded-xl flex items-center justify-center text-2xl
              transition-all hover:scale-110
              ${currentAvatarId === avatar.id 
                ? 'ring-2 ring-offset-2' 
                : 'opacity-70 hover:opacity-100'
              }
            `}
            style={{
              background: currentAvatarId === avatar.id 
                ? themeConfig?.colors?.gradient 
                : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              ringColor: themeConfig?.colors?.primary,
            }}
            title={avatar.name}
          >
            {avatar.emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

// Export avatar options for use elsewhere
export { AVATAR_OPTIONS };

export default TeamAvatar;
